"use client";

import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Menu, X, Mail } from "lucide-react";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  if (loading) {
    return <div className="hidden md:flex gap-1">
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-md" />
      </div>;
  }
  return <>
      <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden hover:!bg-transparent focus:!bg-transparent" aria-label="Toggle menu">
        {mobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
      </Button>

      <NavigationMenu viewport={false} className="hidden md:flex">
        <NavigationMenuList>
          {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="group relative px-4 py-2 text-sm font-medium transition-colors hover:!bg-transparent focus:!bg-transparent hover:text-foreground focus:text-foreground">
                  {item.label}
                  <span className="absolute bottom-0 left-1/2 h-0.5 w-0 bg-primary transition-all duration-300 ease-out-back group-hover:w-full group-hover:left-0 group-focus:w-full group-focus:left-0" />
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>)}
        </NavigationMenuList>
      </NavigationMenu>

      {mobileMenuOpen && <div className="md:hidden absolute top-full left-0 w-full bg-background border-b shadow-medium z-50">
          <nav className="flex flex-col p-4 gap-2">
            {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="px-4 py-3 text-sm font-medium hover:bg-accent/10 rounded-md transition-colors" onClick={() => setMobileMenuOpen(false)}>
                {item.label}
              </Link>)}
          </nav>
        </div>}
    </>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header className={`sticky top-0 z-50 w-full bg-background/95 backdrop-blur-sm border-b transition-all duration-300 ${isScrolled ? "h-16 shadow-soft" : "h-24"}`}>
      <div className="container mx-auto px-4 h-full flex items-center justify-between gap-2 md:gap-8">
        <Link to="/" className="flex-shrink-0">
          <img src="https://evident-urchin.10web.cloud/wp-content/uploads/2026/01/1.stru-con-1.png" alt="Strucon Group" className="!w-[200px] !h-[200px]" />
        </Link>

        <div className="flex items-center gap-2 md:gap-6">
          <MenuProvider menu_id="5">
            <HeaderMenuConsumer />
          </MenuProvider>

          <div className="flex flex-col gap-0.5 md:gap-1 min-w-0">
            <Button asChild size="sm" className="md:h-9 md:px-4 text-xs md:text-sm whitespace-nowrap bg-accent text-foreground hover:bg-accent-hover active:bg-accent-active focus-visible:ring-accent/30 hover:!bg-accent-hover focus:!bg-accent-hover hover:-translate-y-0.5 hover:shadow-medium focus:-translate-y-0.5 focus:shadow-medium transition-all duration-150">
              <Link to="tel:0432061310">Call Now</Link>
            </Button>
            <Link to="mailto:strucon.group@hotmail.com" className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors duration-150 justify-center">
              <Mail className="size-3" />
              strucon.group@hotmail.com
            </Link>
          </div>
        </div>
      </div>
    </header>;
}